import { LegalLayout, LegalSection, LegalList } from "@/components/LegalLayout";

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout
      testId="privacy-policy-page"
      eyebrow="Legal"
      title="Privacy Policy"
      subtitle="How Coffice 24x7 collects, uses, and protects the information you share with us."
      lastUpdated="18 August 2026"
    >
      <LegalSection title="1. Introduction">
        <p>
          Coffice 24x7 ("Coffice", "we", "us", or "our") operates coworking centers across
          Pune and the website at coffice24x7.in. This Privacy Policy explains what personal
          information we collect when you visit our website, enquire about a workspace, or
          use one of our centers, and how we handle that information.
        </p>
        <p>
          By using our website or services, you agree to the practices described in this
          policy. If you do not agree, please stop using the site and contact us with your
          concerns.
        </p>
      </LegalSection>

      <LegalSection title="2. Information We Collect">
        <p>We collect only the information we need to respond to you and run our centers:</p>
        <LegalList
          items={[
            "Contact details you submit — name, email address, phone number, company name, and the message or enquiry you send us.",
            "Booking and membership details — the location, plan or seat type you are interested in, start date, and billing information required to invoice you.",
            "Centre access records — visitor sign-in details and CCTV footage captured in our common areas for the safety and security of members.",
            "Website usage data — pages visited, approximate location, browser and device type, and referring links, collected through standard analytics.",
          ]}
        />
        <p>
          We do not knowingly collect information from anyone under the age of 18, and we do
          not collect sensitive personal data unless it is required by law (for example, ID
          verification for a long-term membership).
        </p>
      </LegalSection>

      <LegalSection title="3. How We Use Your Information">
        <LegalList
          items={[
            "To respond to enquiries, schedule tours, and share pricing or availability.",
            "To set up and manage your membership, day pass, or meeting-room booking.",
            "To issue invoices, process payments, and maintain accounting records.",
            "To send service updates about your workspace, such as access changes or maintenance notices.",
            "To share offers, events, and community news — only where you have opted in, and you can unsubscribe at any time.",
            "To keep our centers secure and to improve the layout, amenities, and content of our website.",
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Cookies and Analytics">
        <p>
          Our website uses cookies and similar technologies to remember your preferences and
          to understand how visitors move through the site. Analytics cookies help us see
          which locations and pages are most useful so we can improve them. You can block or
          delete cookies in your browser settings; some parts of the site may not work as
          expected if you do.
        </p>
      </LegalSection>

      <LegalSection title="5. Sharing Your Information">
        <p>
          We do not sell or rent your personal information. We share it only in these
          limited situations:
        </p>
        <LegalList
          items={[
            "With service providers who work on our behalf — payment gateways, hosting and email providers, and analytics tools — under obligations of confidentiality.",
            "With the building management or landlord of a specific center, where required for access control or security.",
            "Where required by law, court order, or a lawful request from a government authority.",
            "In connection with a merger, acquisition, or transfer of our business, in which case we will notify you of any change in ownership of your data.",
          ]}
        />
      </LegalSection>

      <LegalSection title="6. Data Security and Retention">
        <p>
          We use reasonable technical and organisational measures — including access
          controls, encrypted transmission, and restricted internal access — to protect your
          information. No method of transmission or storage is completely secure, so we
          cannot guarantee absolute security.
        </p>
        <p>
          We keep your information only as long as needed for the purpose it was collected,
          or as required by applicable tax and accounting law. Enquiry data from prospects
          who do not become members is deleted after a reasonable period.
        </p>
      </LegalSection>

      <LegalSection title="7. Your Rights">
        <p>You may, at any time, ask us to:</p>
        <LegalList
          items={[
            "Confirm what personal information we hold about you and provide a copy of it.",
            "Correct information that is inaccurate or out of date.",
            "Delete your information, where we are not required to retain it by law.",
            "Stop sending you marketing communications.",
            "Withdraw a consent you previously gave us.",
          ]}
        />
        <p>
          To exercise any of these rights, email us at{" "}
          <a
            href="mailto:contact@coffice24x7.in"
            className="text-orange-500 font-medium hover:text-orange-600 transition-colors"
          >
            contact@coffice24x7.in
          </a>
          . We will respond within a reasonable time.
        </p>
      </LegalSection>

      <LegalSection title="8. Third-Party Links">
        <p>
          Our website may link to third-party sites, maps, and booking tools. We are not
          responsible for the privacy practices of those sites, and we encourage you to read
          their policies before sharing information with them.
        </p>
      </LegalSection>

      <LegalSection title="9. Changes to This Policy">
        <p>
          We may update this Privacy Policy as our services or legal obligations change. The
          revised version will be posted on this page with a new "last updated" date.
          Continued use of our website or services after an update means you accept the
          revised policy.
        </p>
      </LegalSection>

      <LegalSection title="10. Contact Us">
        <p>
          For any question about this policy or how we handle your data, reach us at:
        </p>
        <p>
          Coffice 24x7, Pune, Maharashtra, India
          <br />
          Email:{" "}
          <a
            href="mailto:contact@coffice24x7.in"
            className="text-orange-500 font-medium hover:text-orange-600 transition-colors"
          >
            contact@coffice24x7.in
          </a>
          <br />
          Phone:{" "}
          <a
            href="tel:+919179748353"
            className="text-orange-500 font-medium hover:text-orange-600 transition-colors"
          >
            +91 91797 48353
          </a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
